import React, { useMemo } from 'react';
import * as d3 from 'd3';

const HalfPieChart = ({ 
  data = [], 
  settings = {}, 
  centerText = "",
  halfPieWidth = 350,
  id,
  sortedPieColorObject
}) => {
  const leftMarginValue = 85; // Space for labels on the left
  const {
    width = halfPieWidth, // Default width accounts for half pie + label area
    height = 500,
    innerRadius = 360,
    outerRadius = 400,
    color = [],
    textSize = '11px',
    legendColorMap = {},
    startAngleDeg = 40,
    endAngleDeg = 140,
  } = settings;

  const containerWidth = document.getElementById(id)?.offsetWidth
  const isSmallContainer = (document.getElementById(id)?.offsetWidth < 1000) || false;
  const getFontSize = () => {
    if (containerWidth < 900) return '11px';
    if (containerWidth < 1000) return '12px';
    return '14px';
  }

  const relaxLabels = (sideLabels) => {
    const padding = 12;
    for (let i = 0; i < 300; i++) {
      let collisions = 0;
      for (let j = 0; j < sideLabels.length - 1; j++) {
        let a = sideLabels[j], b = sideLabels[j + 1];
        const minSpacing = (a.textHeight / 2) + (b.textHeight / 2) + padding;
        let diff = b.posC[1] - a.posC[1];
        if (diff < minSpacing) {
          collisions++;
          let overlap = minSpacing - diff;
          a.posC[1] -= overlap * 0.5;
          b.posC[1] += overlap * 0.5;
          a.posB[1] = a.posC[1]; b.posB[1] = b.posC[1];
        }
      }
      if (collisions === 0) break;
    }
  };

  const { pieData, labels, colorScale, sAngle, eAngle } = useMemo(() => {
    const colorS = d3.scaleOrdinal(color);
    const sortedData = [...data].sort((a, b) => b.value - a.value);

    // Rotation: Start 10 deg, End 170 deg
    const startAngle = (startAngleDeg * Math.PI) / 180; 
    const endAngle = (endAngleDeg * Math.PI) / 180;

    const pieGenerator = d3.pie()
      .value(d => d.value)
      .sort(null)
      .startAngle(startAngle)
      .endAngle(endAngle)
      .padAngle(0.01);

    const calculatedPieData = pieGenerator(sortedData);

    const processedLabels = calculatedPieData
      .filter(d => d.data.value > 0)
      .map((d, i) => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        const angle = midAngle - Math.PI / 2;
        
        const isRight = Math.cos(angle) >= 0;
        const posA = [Math.cos(angle) * outerRadius, Math.sin(angle) * outerRadius];
        const posB = [Math.cos(angle) * (outerRadius * 1.25), Math.sin(angle) * (outerRadius * 1.25)];
        const shelfX = (outerRadius * 1.5) * (isRight ? 1 : -1);
        
        const words = d.data.name.split(" ");
        const lines = words.length > 2 ? [words.slice(0, 2).join(" "), words.slice(2).join(" ")] : [d.data.name];
        
        return { 
          data: d, index: i, posA, posB, posC: [shelfX, posB[1]], 
          isRight, lines, textHeight: lines.length * 14 
        };
      });

    relaxLabels(processedLabels.filter(l => l.isRight).sort((a, b) => a.posC[1] - b.posC[1]));
    relaxLabels(processedLabels.filter(l => !l.isRight).sort((a, b) => a.posC[1] - b.posC[1]));

    return { 
      pieData: calculatedPieData, 
      labels: processedLabels, 
      colorScale: colorS, 
      sAngle: startAngle, 
      eAngle: endAngle 
    };
  }, [data, color, outerRadius]);

  const arcGenerator = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);
  
  // Background Circle Generator: innerRadius same as pie, outer is 20px smaller
  const backgroundArcGenerator = d3.arc()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius - 20);

  const getFillColor = (d, i) => {
 
    if(sortedPieColorObject?.[d.data.name]) return sortedPieColorObject[d.data.name];

    if (legendColorMap[d.data.name]) return legendColorMap[d.data.name];
    return colorScale(i);
  };

  return React.createElement('svg', { 
    width: width, height: height, viewBox: `0 0 ${width} ${height}`, style: { overflow: 'visible' } 
  }, 
    React.createElement('g', { 
      // transform: `translate(${outerRadius + 100}, ${height / 2})` 
      transform: `translate(-${leftMarginValue}, ${height / 2})` 
    },

      // 2. Main Pie Slices
      pieData.map((d, i) => 
        React.createElement('path', {
          key: i,
          d: arcGenerator(d),
          fill: getFillColor(d, i),
          stroke: "#fff",
          strokeWidth: "1"
        })
      ),

      // 1. Background White Circle (rendered behind slices)
      React.createElement('path', {
        d: backgroundArcGenerator({ startAngle: sAngle, endAngle: eAngle }),
        fill: "#FFFFFF",
        stroke: "#FFFFFF", // Light stroke to make white visible against white backgrounds
        strokeWidth: "3"
      }),

      // 3. Center Text
      React.createElement('text', { 
        textAnchor: "start", dy: "0.35em", x: 70, fontWeight: "700", fontSize: "16px", fill: "#37474F" 
      }, centerText),

      // 4. Labels & Connectors
      labels.map((l, i) => {
        const sliceColor = getFillColor(l.data, l.index);
        return React.createElement('g', { key: i },
          React.createElement('polyline', {
            points: `${l.posA} ${l.posB} ${l.posC}`,
            fill: "none", stroke: "#CFD8DC", strokeWidth: "1"
          }),
          React.createElement('circle', { 
            cx: l.posA[0], cy: l.posA[1], r: 3, 
            fill: sliceColor, stroke: "#fff" 
          }),
          React.createElement('text', {
            x: l.posC[0] + (l.isRight ? 6 : -6),
            y: l.posC[1],
            textAnchor: l.isRight ? 'start' : 'end',
            fontSize: isSmallContainer ? getFontSize() : textSize,
            fill: "#546E7A", dy: l.lines.length > 1 ? "-0.3em" : "0.3em"
          }, 
            l.lines.map((line, idx) => 
              React.createElement('tspan', {
                key: idx, x: l.posC[0] + (l.isRight ? 6 : -6), dy: idx > 0 ? '1.1em' : '0em'
              }, 
                l.isRight 
                  ? [
                      line, 
                      idx === 0 ? " " : null, 
                      idx === 0 ? React.createElement('tspan', { key: 'val', fontWeight: "bold", fill: sliceColor }, `${l.data.data.value.toFixed(0)}%`) : null
                    ]
                  : [
                      idx === 0 ? React.createElement('tspan', { key: 'val', fontWeight: "bold", fill: sliceColor }, `${l.data.data.value.toFixed(0)}%`) : null, 
                      idx === 0 ? " " : null, 
                      line
                    ]
              )
            )
          )
        );
      })
    )
  );
};

export default HalfPieChart;